import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LivePal, GearPal, GearItem } from "../types/Pal";
import { FaTimes, FaPencilAlt } from "react-icons/fa";
import AddButton from "./AddButton";
import { deleteLivepal, getMyLivepals } from "../backend/api/livepal";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import PalPopup from "./PalPopup/PalPopup";
import {
  deleteGearpal,
  getMyGearpals,
  deleteGearItem,
} from "../backend/api/gearlpal";
import {
  IoTrashOutline,
  IoChevronBack,
  IoChevronForward,
} from "react-icons/io5";
import EditGearItemModal from "./EditGearItemModal";
import AddGearItemModal from "./AddGearItemModal";
import AddOrEditPalForm from "./AddOrEditPalForm/AddOrEditPalForm";
import { useUserAddress } from "./providers/ModalsProvider";

interface MyPalsProps {
  gearPals: GearPal[];
  currentUser: string | null;
  onAddVenue: () => void;
  onAddGear: () => void;
}

const MyPals: React.FC<MyPalsProps> = ({
  gearPals,
  currentUser,
  onAddVenue,
  onAddGear,
}) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const [selectedPal, setSelectedPal] = useState<LivePal | GearPal | null>(
    null
  );

  // State for item editing
  const [editingItem, setEditingItem] = useState<{
    item: GearItem;
    palId: number;
  } | null>(null);

  // State for adding item
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);

  // Pagination state for gear items
  const [gearItemIndex, setGearItemIndex] = useState(0);
  const { open: openUserAddressModal } = useUserAddress();
  const [livePalIndex, setLivePalIndex] = useState(0);
  const [editingPal, setEditingPal] = useState<LivePal | GearPal | null>(null);
  const [forcedGearpalIdForItems, setForcedGearpalIdForItems] = useState<
    number | null
  >(null);

  const { mutate: deleteLivepalMutation } = useMutation({
    mutationFn: (id: number) => deleteLivepal(id, i18n.language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["livepals"] });
      queryClient.invalidateQueries({ queryKey: ["my-livepals"] });
      toast.success(t("pal.deleted", "Pal eliminata con successo"));
    },
    onError: () => {
      toast.error(t("pal.deleteFailed"));
    },
  });

  const { mutate: deleteGearpalMutation } = useMutation({
    mutationFn: (id: number) => deleteGearpal(id, i18n.language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
      queryClient.invalidateQueries({ queryKey: ["gearpals"] });
      toast.success(
        t("pal.gearDeleted", "Attrezzatura eliminata con successo")
      );
    },
    onError: () => {
      toast.error(t("pal.deleteFailed"));
    },
  });

  const { mutate: deleteItemMutation } = useMutation({
    mutationFn: (id: number) => deleteGearItem(id, i18n.language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
      queryClient.invalidateQueries({ queryKey: ["gearpals"] });
      toast.success(t("item.deleted", "Articolo eliminato con successo"));
    },
    onError: () => {
      toast.error(
        t("item.deleteFailed", "Errore durante l'eliminazione dell'articolo")
      );
    },
  });

  if (!currentUser) return null;

  const { data: myLivePals } = useQuery({
    queryKey: ["my-livepals"],
    queryFn: () => getMyLivepals(i18n.language),
  });

  const { data: myGearPals } = useQuery({
    queryKey: ["my-gearpals"],
    queryFn: () => getMyGearpals(i18n.language),
  });

  // Flatten items from all gearpals
  const myGearItems =
    myGearPals?.flatMap((pal: GearPal) =>
      (pal.items || []).map((item: GearItem) => ({
        ...item,
        palId: pal.id,
        palName: pal.name,
      }))
    ) || [];

  const visibleGearItems = myGearItems.slice(gearItemIndex, gearItemIndex + 2);

  const nextGearItems = () => {
    if (gearItemIndex + 2 < myGearItems.length) {
      setGearItemIndex(gearItemIndex + 2);
    }
  };

  const visibleLivePals =
    myLivePals?.slice(livePalIndex, livePalIndex + 2) || [];

  const nextLivePals = () => {
    if (livePalIndex + 2 < (myLivePals?.length || 0)) {
      setLivePalIndex(livePalIndex + 2);
    }
  };

  const prevLivePals = () => {
    if (livePalIndex - 2 >= 0) {
      setLivePalIndex(livePalIndex - 2);
    }
  };

  const prevGearItems = () => {
    if (gearItemIndex - 2 >= 0) {
      setGearItemIndex(gearItemIndex - 2);
    }
  };

  const handleAddGearClick = () => {
    if (myGearPals && myGearPals.length > 0) {
      setIsAddItemOpen(true);
    } else {
      openUserAddressModal(false, null, (newPal) => {
        if (newPal && newPal.id) {
          setForcedGearpalIdForItems(newPal.id);
          setIsAddItemOpen(true);
        }
      });
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="absolute top-20 w-[169px] right-4 z-[900] flex items-center justify-center gap-1.5 h-[42px] text-white text-[14px] leading-[92%] rounded-pill shadow-lg bg-secondary hover:bg-secondary-600 transition-all hover:scale-[1.02]"
      >
        <img
          src={"/icons/star.svg"}
          alt="Star"
          className="h-4 object-contain brightness-0 invert"
        />
        <span className="mt-0.5 font-medium">{t("navigation.myPals")}</span>
      </button>
    );
  }

  return (
    <div className="absolute top-[104px] right-4 z-[1001] bg-[#2D1B4E] border border-[#6E11B04D] rounded-modal shadow-2xl w-80 max-h-[calc(100vh-8rem)] overflow-hidden text-white animate-scale-in">
      <div className="overflow-y-auto custom-scrollbar max-h-[calc(100vh-8rem)] p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-[18px] font-bold leading-none">
              {t("navigation.myPals")}
            </h2>
            <p className="text-[12px] text-[#D1D5DC] mt-1">
              {t("myPals.manage", "Gestisci i tuoi contenuti")}
            </p>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Bottoni per aggiungere nuovi elementi */}
        <div className="mb-8 p-4 bg-[#3A1A60] rounded-xl border border-[#6E11B04D]">
          <h3 className="text-sm font-semibold mb-3 text-[#D1D5DC]">
            {t("myPals.addTitle", "Aggiungi nuovo")}
          </h3>
          <AddButton
            onAddVenue={onAddVenue}
            onAddGear={handleAddGearClick}
            hasExistingGear={myGearPals && myGearPals.length > 0}
          />
        </div>

        <div className="space-y-6">
          {myLivePals?.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-[14px] font-bold text-[#D1D5DC] uppercase tracking-wider">
                  {t("myPals.venues", "I Miei Locali")}
                </h3>

                {myLivePals.length > 2 && (
                  <div className="flex gap-1.5">
                    <button
                      onClick={prevLivePals}
                      disabled={livePalIndex === 0}
                      className="p-1 rounded-full bg-white/5 text-white disabled:opacity-20 hover:bg-white/10 transition-colors border border-white/10"
                    >
                      <IoChevronBack size={14} />
                    </button>
                    <button
                      onClick={nextLivePals}
                      disabled={livePalIndex + 2 >= myLivePals.length}
                      className="p-1 rounded-full bg-white/5 text-white disabled:opacity-20 hover:bg-white/10 transition-colors border border-white/10"
                    >
                      <IoChevronForward size={14} />
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {visibleLivePals.map((pal: LivePal, index: number) => (
                  <div
                    key={`livePal-${pal.id}-${index}`}
                    className="p-4 bg-[#3A1A60] border border-[#6E11B04D] rounded-xl hover:bg-[#3D1E6C] transition-all group"
                  >
                    <div className="flex justify-between items-start">
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => {
                          setSelectedPal(pal);
                        }}
                      >
                        <p className="font-bold text-[15px]">{pal.name}</p>
                        <p className="text-[13px] text-gray-400 mt-0.5">
                          {pal.city}, {pal.region}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              t("common.confirmDelete", "Sei sicuro?")
                            )
                          ) {
                            deleteLivepalMutation(pal.id as number);
                          }
                        }}
                        className="text-red-400 hover:text-red-500 transition-colors p-1 opacity-60 group-hover:opacity-100"
                      >
                        <IoTrashOutline size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {myGearItems.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-[14px] font-bold text-[#D1D5DC] uppercase tracking-wider">
                  {t("myPals.gearItems", "I Miei Articoli")}
                </h3>

                {myGearItems.length > 2 && (
                  <div className="flex gap-1.5">
                    <button
                      onClick={prevGearItems}
                      disabled={gearItemIndex === 0}
                      className="p-1 rounded-full bg-white/5 text-white disabled:opacity-20 hover:bg-white/10 transition-colors border border-white/10"
                    >
                      <IoChevronBack size={14} />
                    </button>
                    <button
                      onClick={nextGearItems}
                      disabled={gearItemIndex + 2 >= myGearItems.length}
                      className="p-1 rounded-full bg-white/5 text-white disabled:opacity-20 hover:bg-white/10 transition-colors border border-white/10"
                    >
                      <IoChevronForward size={14} />
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {visibleGearItems.map((item: any, index: number) => (
                  <div
                    key={`gearItem-${item.id}-${index}`}
                    className="p-4 bg-[#3A1A60] border border-[#6E11B04D] rounded-xl hover:bg-[#3D1E6C] transition-all group"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-bold text-[15px]">{item.name}</p>
                        <p className="text-[11px] text-secondary/70 mt-0.5 uppercase tracking-wide">
                          {item.label || item.category}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingItem({ item, palId: item.palId });
                          }}
                          className="text-secondary hover:text-white transition-colors p-1 opacity-60 group-hover:opacity-100"
                        >
                          <FaPencilAlt size={14} />
                        </button>
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                t("common.confirmDelete", "Sei sicuro?")
                              )
                            ) {
                              deleteItemMutation(item.id);
                            }
                          }}
                          className="text-red-400 hover:text-red-500 transition-colors p-1 opacity-60 group-hover:opacity-100"
                        >
                          <IoTrashOutline size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {myLivePals?.length === 0 && myGearItems.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400 text-sm">
              {t("myPals.nothingFound", "Nessun locale o attrezzatura trovati")}
            </p>
          </div>
        )}

        {selectedPal && (
          <PalPopup pal={selectedPal} onClose={() => setSelectedPal(null)} />
        )}

        {editingItem && (
          <EditGearItemModal
            isOpen={true}
            onClose={() => setEditingItem(null)}
            item={editingItem.item}
          />
        )}

        {isAddItemOpen &&
          (forcedGearpalIdForItems ||
            (myGearPals && myGearPals.length > 0)) && (
            <AddGearItemModal
              isOpen={true}
              onClose={() => {
                setIsAddItemOpen(false);
                setForcedGearpalIdForItems(null);
              }}
              gearpalId={
                (forcedGearpalIdForItems ||
                  (myGearPals && myGearPals.length > 0
                    ? myGearPals[0].id
                    : 0)) as number
              }
            />
          )}

        {editingPal && (
          <AddOrEditPalForm
            isOpen={true}
            onClose={() => setEditingPal(null)}
            onSubmitSuccess={(updated) => {
              if (updated) {
                // Handle update if needed, though queries will be invalidated
              }
              setEditingPal(null);
            }}
            palType={editingPal.type as any}
            isEdit={true}
            palToEdit={editingPal}
          />
        )}
      </div>
    </div>
  );
};

export default MyPals;
